import { Icon, Box } from 'vopay';
import { useHistory } from 'react-router-dom';
import ElinxForm from './ElinxForm';
import { ElinxInfo } from './types';
import usePostSendPaylink from '@/hooks/useApi/usePostSendPaylink';
import usePostEmbedUrl from '@/hooks/useApi/usePostEmbedUrl';
import useUploadDocument from '@/hooks/useApi/useUploadDocument';
import { useDataFetchIndicator } from '@/hooks';
import { useConfirmDialog } from '@/providers/ConfirmDialog.provider';
import { Title } from 'UI';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/providers/Toast.provider';
import useContact from './useContact';

const ElinxCollectContainer: React.FC = () => {
  const history = useHistory();
  const toast = useToast();
  const [, , , sendElinx] = usePostSendPaylink({});
  const [, , , postEmbedUrl] = usePostEmbedUrl({});
  const confirmDialog = useConfirmDialog();
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.ECollect' });
  const { t: tElinx } = useTranslation('translations', { keyPrefix: 'Elinx' });
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const { uploadDocument } = useUploadDocument();
  const { getContact, saveContactEmail } = useContact();

  const onSubmit = async (values: ElinxInfo) => {
    try {
      setDataFetchingInfo(true, 'loading some data', 'fullScreen');

      const { InvoiceFile, InvoiceName, ...rest } = values;

      const embedUrlResponse = await postEmbedUrl({
        ...rest,
        RedirectURL: '',
        RedirectMethod: 'JavascriptMessage',
        ClientControlled: false,
        Language: 'en',
        Version: 'v2',
        ClientAccountID: values?.Contact,
      });

      if (!embedUrlResponse.Success) {
        throw new Error(embedUrlResponse.ErrorMessage);
      }

      const hasInvoice = InvoiceFile !== '' && InvoiceName !== '';
      const documentID = hasInvoice
        ? (
            await uploadDocument({
              DocumentContent: InvoiceFile || '',
              DocumentName: InvoiceName || '',
              DocumentType: 'invoice',
            })
          ).DocumentID
        : '';

      const elinxResponse = await sendElinx({
        ...rest,
        IframeKey: embedUrlResponse.IframeKey,
        ReceiverEmailAddress: values.OverrideEmailAddress || values.NewEmailAddress || '',
        ReceiverName: values?.ExistingContactName || `${values.FirstName || ''} ${values.LastName || ''}`,
        Amount: values.Amount ? Number(values.Amount) : undefined,
        PaymentType: 'fund',
        ClientAccountID: values?.Contact,
        ReceiverPhoneNumber: values?.ReceiverPhoneNumber?.replace('+', ''),
        PaymentMethodDisplayOptions:
          values?.PaymentMethodDisplayOptions !== 'Existing' && values?.PaymentMethodDisplayOptions !== 'New'
            ? 'Both'
            : values?.PaymentMethodDisplayOptions,
        BankingPaymentRail: values.PaymentSelectionMethod?.includes('bank') ? values.BankingPaymentRail : undefined,
        DocumentID: documentID,
      });
      if (!elinxResponse.Success) {
        throw new Error(elinxResponse.ErrorMessage);
      }

      const contactResponse = await getContact({
        ContactID: values.Contact,
      });

      if (contactResponse.EmailAddress === '' && values.OverrideEmailAddress) {
        try {
          await saveContactEmail({
            ContactID: values.Contact,
            EmailAddress: values.OverrideEmailAddress,
          });
        } catch (err) {
          toast.neutral(tElinx('savingEmailError', { defaultValue: 'Failed to save the email address' }));
        }
      }

      const sendNotificationMethod = values?.SendNotification ? values?.NotificationMethod : 'none';
      history.push(
        `/elinx-history/collect/confirmation/${elinxResponse.ELinxRequestID}/${sendNotificationMethod}/none`,
      );
    } catch (err) {
      console.error(err);
      const subTitle = err instanceof Error ? err.message : undefined;

      confirmDialog.error(t('FetchingInfoErrorDialog.title'), {
        subTitle,
        cancelLabel: t('FetchingInfoErrorDialog.cancelLabel'),
      });
    } finally {
      setDataFetchingInfo(false);
    }
  };
  return (
    <Box flx="row" jc="center">
      <Box flx="col" jc="center" maxwidth="1121px" boxWidth="100%" p="md">
        <Box flx="col" gapSize="32px">
          <Title>
            <Box ai="baseline" gapSize="4px">
              <Icon icon="eLinxE" height="18px" width="18px" />
              {t('title')}
            </Box>
          </Title>
          <div>{tElinx('Cards.ECollect.description')}</div>
        </Box>

        <Box base="8" pt="xmd">
          <ElinxForm onSubmit={onSubmit} formType="Collect" />
        </Box>
      </Box>
    </Box>
  );
};

export default ElinxCollectContainer;
